import { useState } from '#imports'
import { type MaybeRefOrGetter, onUnmounted, toValue, watch } from 'vue'

import { omit } from '@backmarket/utils/object/omit'

const REGISTRY_KEY = 'shopping-assistant-structured-data-registry'

export type ShoppingAssistantStructuredData = Record<string, unknown> & {
  '@type': string
}

function getTypeKey(data: ShoppingAssistantStructuredData): string {
  return data['@type']
}

/**
 * Structured data (JSON-LD) captured from the current page, keyed by
 * schema.org @type, so the shopping-assistant browsing context can describe
 * what the customer is looking at regardless of which page produced it.
 * A page-scoped useState so it survives client-side navigation within the
 * same app instance without leaking across SSR requests.
 */
export function useShoppingAssistantStructuredDataRegistry() {
  const registry = useState<Record<string, object>>(REGISTRY_KEY, () => ({}))

  /**
   * Records a JSON-LD object under its @type, replacing any previous object
   * of the same type.
   */
  function register(data: ShoppingAssistantStructuredData) {
    if (!import.meta.client) return

    registry.value = { ...registry.value, [getTypeKey(data)]: data }
  }

  /**
   * Removes a previously registered @type, e.g. when a page's structured
   * data goes back to null (PDP offer flips out-of-stock) without a full
   * navigation resetting the registry.
   */
  function unregister(type: string) {
    if (!import.meta.client) return

    registry.value = omit(registry.value, [type])
  }

  /**
   * Clears the registry. Called at the start of each navigation so a
   * slow-to-declare page never inherits the previous page's structured data.
   */
  function reset() {
    if (!import.meta.client) return

    registry.value = {}
  }

  return { registry, register, unregister, reset }
}

/**
 * Registers a page's JSON-LD with the shopping-assistant structured-data
 * registry as `source` resolves, without leaking how/when that happens into
 * the caller (e.g. a page's own useHead call). `flush: 'post'` keeps the
 * write off any other reactive getter's read phase — a PDP `useHead(() =>
 * ({...}))` getter once wrote to this registry synchronously from inside its
 * own read, which fed unhead's dirty-tracking loop back into itself and hung
 * the tab.
 */
export function useRegisterShoppingAssistantStructuredData(
  source: MaybeRefOrGetter<ShoppingAssistantStructuredData | null | undefined>,
) {
  const { register, unregister } = useShoppingAssistantStructuredDataRegistry()
  let lastType: string | null = null

  watch(
    () => toValue(source),
    (data) => {
      if (data) {
        register(data)
        lastType = getTypeKey(data)
      } else if (lastType) {
        unregister(lastType)
        lastType = null
      }
    },
    { flush: 'post', immediate: true },
  )

  onUnmounted(() => {
    if (lastType) unregister(lastType)
  })
}
